/**
 * Números pseudoaleatórios determinísticos (ADR 0004). Só usa operações inteiras de 32 bits
 * (`Math.imul`, deslocamentos, xor), com resultado idêntico em qualquer motor JavaScript.
 */

/** Mistura de 32 bits "lowbias32" (Chris Wellons): boa avalanche, sem estado. */
export function mix32(value: number): number {
  let x = value >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x7feb352d);
  x ^= x >>> 15;
  x = Math.imul(x, 0x846ca68b);
  x ^= x >>> 16;
  return x >>> 0;
}

/** Combina vários inteiros num hash de 32 bits sem sinal. A ordem importa. */
export function hash32(...values: readonly number[]): number {
  let h = 0x9e3779b9;
  for (const value of values) {
    h = mix32((h ^ mix32(value | 0)) + 0x6a09e667);
  }
  return h;
}

/** Hash FNV-1a de 32 bits para textos (rótulos de sistema, seeds digitadas no lobby). */
export function hashString(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return mix32(h);
}

/**
 * Deriva uma seed independente para um sistema/chunk. Assim a ordem em que os sistemas
 * geram conteúdo não altera o resultado (ex.: `deriveSeed(seed, "recursos", cx, cz)`).
 */
export function deriveSeed(seed: number, label: string, ...coords: readonly number[]): number {
  return hash32(seed, hashString(label), ...coords);
}

/** Converte um inteiro de 32 bits em float uniforme em [0, 1) com 32 bits de resolução. */
export function toUnitFloat(value: number): number {
  return (value >>> 0) / 4294967296;
}

/** Gerador SFC32 (Chris Doty-Humphrey): rápido, período ≥ 2^32, estado de 128 bits. */
export class Rng {
  private a: number;
  private b: number;
  private c: number;
  private d: number;

  constructor(seed: number) {
    this.a = mix32(seed);
    this.b = mix32(seed ^ 0x85ebca6b);
    this.c = mix32(seed ^ 0xc2b2ae35);
    this.d = 1;
    // Descarta as primeiras saídas para espalhar seeds parecidas.
    for (let i = 0; i < 12; i++) this.nextUint32();
  }

  nextUint32(): number {
    const t = (((this.a + this.b) | 0) + this.d) | 0;
    this.d = (this.d + 1) | 0;
    this.a = this.b ^ (this.b >>> 9);
    this.b = (this.c + (this.c << 3)) | 0;
    this.c = (this.c << 21) | (this.c >>> 11);
    this.c = (this.c + t) | 0;
    return t >>> 0;
  }

  /** Float uniforme em [0, 1). */
  next(): number {
    return toUnitFloat(this.nextUint32());
  }

  /** Float uniforme em [min, max). */
  range(min: number, max: number): number {
    return min + (max - min) * this.next();
  }

  /**
   * Inteiro uniforme em [min, max] (inclusivo), sem viés: rejeita os sorteios do resto que
   * sobraria na divisão de 2^32 pela largura. Largura máxima: 2^32.
   */
  int(min: number, max: number): number {
    if (!Number.isSafeInteger(min) || !Number.isSafeInteger(max) || max < min) {
      throw new RangeError(`int exige inteiros min <= max (recebido ${min}, ${max})`);
    }
    const width = max - min + 1;
    if (width > 4294967296) throw new RangeError("int aceita largura de no máximo 2^32");
    if (width === 4294967296) return min + this.nextUint32();
    const limit = 4294967296 - (4294967296 % width);
    let value = this.nextUint32();
    while (value >= limit) value = this.nextUint32();
    return min + (value % width);
  }

  /** `true` com probabilidade `p`. */
  chance(p: number): boolean {
    return this.next() < p;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new RangeError("pick em lista vazia");
    return items[this.int(0, items.length - 1)] as T;
  }

  /** Índice sorteado proporcionalmente aos pesos (pesos ≤ 0 nunca saem; pesos não finitos são erro). */
  weighted(weights: readonly number[]): number {
    let total = 0;
    for (const w of weights) {
      if (!Number.isFinite(w)) throw new RangeError(`weighted com peso não finito: ${w}`);
      if (w > 0) total += w;
    }
    if (!Number.isFinite(total)) throw new RangeError("weighted: soma dos pesos estourou");
    if (total <= 0) throw new RangeError("weighted sem peso positivo");
    let roll = this.next() * total;
    for (let i = 0; i < weights.length; i++) {
      const w = weights[i] as number;
      if (w <= 0) continue;
      roll -= w;
      if (roll < 0) return i;
    }
    // Arredondamento: devolve o último índice com peso positivo.
    for (let i = weights.length - 1; i >= 0; i--) if ((weights[i] as number) > 0) return i;
    return 0;
  }

  /** Embaralha no lugar (Fisher–Yates) e devolve o mesmo array. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = this.int(0, i);
      const tmp = items[i] as T;
      items[i] = items[j] as T;
      items[j] = tmp;
    }
    return items;
  }
}
